/**
 * The project's API tab (vfs-service src/apitester, in the pod where the project runs): its services' endpoints, the
 * app's own users ("Sign in as"), test users, and one call sent from the server to a running service. Every call goes
 * through vfs-service's base URL with the person's authorized fetch, as DataService does. The person's Flow sign-in
 * stays with vfs-service: it is never sent on to the app.
 *
 * - `getEndpoints(projectId)` → `{ canWrite, services: [{ service, name, running, state, source, auth, previewUrl,
 *   endpoints: [{ method, path, summary, params, body?, auth, origin, file?, line? }] }] }`
 * - `getAppUsers(projectId, service)` → `{ service, auth, source, users: [{ id, email, name, roles, testUser, signedIn }] }`
 * - `createTestUser(projectId, { service, role?, name? })` → `{ user, note? }` (editors)
 * - `signIn(projectId, { service, userId, password? })` → `{ signedIn, userId, user? }`
 * - `sendRequest(projectId, input)` → `{ status, statusText, headers, body, bodyEncoding, contentType, size,
 *   truncated, ms, logs, sent, as, signedOut? }`
 * - `getGrpcMethods(projectId, service)` → `{ service, files, services: [{ name, methods: [{ name, path, requestType,
 *   responseType, clientStreaming, serverStreaming, example }] }], errors }` (from the service's .proto files)
 * - `sendGrpc(projectId, { service, method, message, metadata?, port? })` → `{ code, codeName, message, response,
 *   responses, headers, trailers, ms, sent }` (editors; sent as gRPC-Web through the preview runner)
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const apiUrl = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}/api${path}`;

/** An HTTP error with the status and the JSON body vfs-service answered (`{ error, code? }`). */
export class ApiTesterError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiTesterError';
    this.status = status;
    this.body = body;
    this.code = body?.code || null;
  }
}

async function call(url, { method = 'GET', body } = {}) {
  if (!base()) { throw new ApiTesterError('vfs-service is not configured', 0, null); }
  const json = body !== undefined;
  const response = await getAuthorizedFetch()(url, {
    method,
    headers: { Accept: 'application/json', ...buildAclContextHeaders(), ...(json ? { 'Content-Type': 'application/json' } : {}) },
    ...(json ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) { throw new ApiTesterError(data?.error || data?.message || `Request failed (${response.status})`, response.status, data); }
  return data || {};
}

export function getEndpoints(projectId) {
  return call(apiUrl(projectId, '/endpoints'));
}

export function getAppUsers(projectId, service) {
  return call(apiUrl(projectId, `/users?${new URLSearchParams({ service })}`));
}

export function createTestUser(projectId, { service, role, name } = {}) {
  return call(apiUrl(projectId, '/users'), { method: 'POST', body: { service, ...(role ? { role } : {}), ...(name ? { name } : {}) } });
}

export function signIn(projectId, { service, userId, password }) {
  return call(apiUrl(projectId, '/users/sign-in'), { method: 'POST', body: { service, userId, ...(password ? { password } : {}) } });
}

export function sendRequest(projectId, input) {
  return call(apiUrl(projectId, '/send'), { method: 'POST', body: input });
}

export function getGrpcMethods(projectId, service) {
  return call(apiUrl(projectId, `/grpc?${new URLSearchParams({ service })}`));
}

export function sendGrpc(projectId, input) {
  return call(apiUrl(projectId, '/grpc/send'), { method: 'POST', body: input });
}
